// Hooks para el módulo projects — sin react-query (no es dep del repo).
// Mismo pattern que expo-capture: estado local + refetch manual.

import { useCallback, useEffect, useState } from 'react';

import { projectsApi, ProjectsApiError } from '../api';
import type {
  HoursSummaryOut,
  MilestoneOut,
  ProjectOut,
  ProjectStatus,
  TimeEntryOut,
} from '../types';

function errorMessage(e: unknown): string {
  if (e instanceof ProjectsApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

export interface UseProjectsResult {
  data: ProjectOut[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjects(status?: ProjectStatus): UseProjectsResult {
  const [data, setData] = useState<ProjectOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await projectsApi.list(status));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export interface UseProjectDetailResult {
  data: ProjectOut | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjectDetail(projectId: string | null): UseProjectDetailResult {
  const [data, setData] = useState<ProjectOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!projectId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await projectsApi.get(projectId));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export interface UseTimeEntriesResult {
  data: TimeEntryOut[] | null;
  summary: HoursSummaryOut | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useTimeEntries(projectId: string | null): UseTimeEntriesResult {
  const [data, setData] = useState<TimeEntryOut[] | null>(null);
  const [summary, setSummary] = useState<HoursSummaryOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!projectId) {
      setData(null);
      setSummary(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [entries, sum] = await Promise.all([
        projectsApi.listTimeEntries(projectId),
        projectsApi.hoursSummary(projectId),
      ]);
      setData(entries);
      setSummary(sum);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, summary, loading, error, refetch };
}

export interface UseMilestoneActionResult {
  complete: (projectId: string, milestoneId: string, note?: string) => Promise<MilestoneOut | null>;
  loading: boolean;
  error: string | null;
}

export function useCompleteMilestone(): UseMilestoneActionResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = useCallback(
    async (projectId: string, milestoneId: string, note?: string) => {
      setLoading(true);
      setError(null);
      try {
        return await projectsApi.completeMilestone(projectId, milestoneId, { note });
      } catch (e) {
        setError(errorMessage(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { complete, loading, error };
}

export interface UseInvoiceNowResult {
  request: (projectId: string) => Promise<unknown | null>;
  loading: boolean;
  error: string | null;
}

export function useInvoiceNow(): UseInvoiceNowResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(async (projectId: string) => {
    setLoading(true);
    setError(null);
    try {
      return await projectsApi.invoiceNow(projectId);
    } catch (e) {
      setError(errorMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { request, loading, error };
}
